import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from "@/components/ui/input";
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Server-side search input for selecting items.
 * 
 * Mode 1 (Stock search): branch + type provided → searches items with stock > 0
 * Mode 2 (SKU search): searchEndpoint provided → searches buyer SKUs from catalog
 * 
 * Props:
 *   branch (string) - Branch name (for stock search mode)
 *   type (string) - "RM" or "FG" (for stock search mode)
 *   searchEndpoint (string) - Custom endpoint URL (overrides stock search)
 *   value (string) - Currently selected item_id
 *   onSelect (fn) - Called with { item_id, name, current_stock } when item selected
 *   disabled (boolean)
 *   placeholder (string)
 *   excludeIds (string[]) - Item IDs to exclude from results
 *   testId (string) - data-testid
 *   minChars (number) - Min chars before search triggers (default 2)
 *   includeFreight (boolean) - Append synthetic "FREIGHT" service-charge
 *                              option when the query matches its name. Used
 *                              by Dispatch + RM Inward to let users add a
 *                              freight charge as a regular line item.
 *   resolveItem (async fn) - Optional fallback: called as `resolveItem(query)`
 *                            when the primary search returns ZERO results.
 *                            If it returns `{ item_id, name, current_stock, ... }`,
 *                            that row is injected into the dropdown so the
 *                            user can select an off-stock SKU that isn't
 *                            indexed by /stock-search (e.g. zero FG
 *                            inventory at this branch). Solves the
 *                            "typed valid SKU, dropdown empty, name blank"
 *                            UX dead-end on the Dispatch line items.
 */
const StockItemSearch = ({ branch, type = "RM", searchEndpoint, value, onSelect, disabled, placeholder, excludeIds = [], testId, minChars = 2, includeFreight = false, resolveItem = null }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  // Disambiguates the "no items" case for the user. Driven by the
  // `query_status` field returned by /api/stock-search:
  //   • 'no_inventory'  → SKU/RM exists & is configured for this branch
  //                       but has zero stock.
  //   • 'no_match'      → SKU/RM not in master OR not configured for branch.
  //   • '' (default)    → no search has run yet or results are non-empty.
  const [queryStatus, setQueryStatus] = useState('');
  // Portal positioning — when the dropdown renders to `document.body`
  // instead of inline, we need to compute its viewport rect from the
  // wrapper so it floats below the input across modal / table / etc.
  // containers that would otherwise clip an `absolute` child.
  const [dropdownRect, setDropdownRect] = useState(null);
  const debounceRef = useRef(null);
  // Feb 2026 — reqId stale-response guard. Any response whose reqId
  // isn't the latest is dropped. Kills defect A (race on this
  // component's per-keystroke search).
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Outside-click closes dropdown. The dropdown is portaled to
  // document.body so we now check BOTH the wrapper (input) and the
  // dropdown ref — clicks on either should keep it open.
  useEffect(() => {
    const handleClick = (e) => {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrapper && !inDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Recompute the dropdown's fixed-position rect when it's open, AND
  // on scroll/resize so it tracks the input through page interaction.
  // `useLayoutEffect` is unnecessary — the dropdown is z-50 overlay-
  // class, a single frame of stale position is invisible.
  useEffect(() => {
    if (!open) {
      setDropdownRect(null);
      return undefined;
    }
    const recompute = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownRect({
        top: r.bottom + 4,   // 4px gap, matches the old `mt-1` (mt-1 ≈ 0.25rem ≈ 4px)
        left: r.left,
        width: r.width,
      });
    };
    recompute();
    // `true` capture is important — scrolls happen on ancestor scroll
    // containers (modal body, table wrapper) not just window.
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open]);

  // Update label when value changes externally
  useEffect(() => {
    if (value && !selectedLabel) {
      setSelectedLabel(value);
    }
    if (!value) {
      // Clear the committed label but DO NOT clear `query` — the user
      // may be actively typing (their backspace just cleared the parent
      // value via `onSelect({item_id: ''})`). Clobbering query here
      // would empty the input mid-edit and the dropdown would never
      // render for the new keystroke.
      setSelectedLabel('');
    }
  }, [value]);

  const search = useCallback(async (q) => {
    if (q.length < minChars) {
      setResults([]);
      setQueryStatus('');
      return;
    }
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let items = [];
      let status = '';

      if (searchEndpoint) {
        // Custom endpoint mode — append q as query parameter
        const separator = searchEndpoint.includes('?') ? '&' : '?';
        const url = `${searchEndpoint}${separator}q=${encodeURIComponent(q)}&limit=20`;
        const res = await axios.get(url, { headers });
        const data = res.data;
        // Normalize: could be array or { items: [] } or { buyer_skus: [] }
        const rawItems = Array.isArray(data) ? data : (data.items || data.buyer_skus || []);
        items = rawItems.map(s => ({
          item_id: s.buyer_sku_id || s.sku_id || s.item_id,
          name: s.name || s.description || '',
          current_stock: s.current_stock || s.quantity || null
        }));
      } else {
        // Stock search mode
        if (!branch) { setResults([]); setQueryStatus(''); setLoading(false); return; }
        const res = await axios.get(`${API}/stock-search`, {
          params: { branch, type, q, limit: 20 },
          headers
        });
        items = res.data.items || [];
        status = res.data.query_status || '';
      }

      const filtered = items.filter(i => !excludeIds.includes(i.item_id)).slice(0, 20);

      // Inject the synthetic FREIGHT + TRANSPORTATION_COST service-charge
      // entries when the parent opts in. Both are exact-name matches on
      // the Zoho side (Freight → "Freight" item, Transportation Cost →
      // "Transportation Cost" item — the near-identical inactive item
      // "Transportation Charges" MUST NOT match; we key off exact
      // sentinel strings on the client too, no fuzzy match, so the UI
      // pill never renders the wrong catalog item.
      const qLower = (q || "").toLowerCase().trim();
      const freightMatches =
        includeFreight &&
        qLower.length >= minChars &&
        ("freight".includes(qLower) || qLower.includes("freight") || qLower === "fr") &&
        !excludeIds.includes("FREIGHT") &&
        !filtered.some(i => i.item_id === "FREIGHT");
      if (freightMatches) {
        filtered.unshift({
          item_id: "FREIGHT",
          name: "Freight (service charge)",
          current_stock: null,
          is_freight: true,
        });
      }
      const transportMatches =
        includeFreight &&
        qLower.length >= minChars &&
        ("transportation cost".includes(qLower) || qLower.includes("transport") || qLower === "tc") &&
        !excludeIds.includes("TRANSPORTATION_COST") &&
        !filtered.some(i => i.item_id === "TRANSPORTATION_COST");
      if (transportMatches) {
        filtered.unshift({
          item_id: "TRANSPORTATION_COST",
          name: "Transportation Cost (service charge)",
          current_stock: null,
          is_transportation_cost: true,
        });
      }

      // Off-stock fallback — when the primary search returns nothing AND
      // a `resolveItem` callback was provided, ask the server to resolve
      // the typed text directly against the master collection. This is
      // what backs the Dispatch "type a SKU with zero FG inventory at
      // this branch" case: /stock-search returns 0 (it filters
      // current_stock > 0), but /item-resolve still surfaces the master
      // record so the user can pick the row, get a name, and a
      // price_master rate.
      if (filtered.length === 0 && resolveItem && q.trim()) {
        try {
          const resolved = await resolveItem(q.trim());
          if (resolved && resolved.item_id) {
            filtered.push({
              item_id: resolved.item_id,
              name: resolved.name || resolved.item_id,
              current_stock: resolved.current_stock != null ? resolved.current_stock : 0,
              // Tag with `off_stock` so the row renderer can flag this
              // as a special "zero inventory" pick (vs a normal in-stock
              // hit) and the user understands why no quantity number
              // appears on the right side.
              off_stock: true,
              // Pass through enrichment fields so the parent's onSelect
              // gets prefilled_rate / rate_source / hsn_code / unit
              // without a second roundtrip.
              prefilled_rate: resolved.prefilled_rate,
              prefilled_tax_id: resolved.prefilled_tax_id,
              rate_source: resolved.rate_source,
              hsn_code: resolved.hsn_code,
              unit: resolved.unit,
            });
            status = 'off_stock';
          }
        } catch {
          // Fall through to the normal "no results" message — non-fatal.
        }
      }

      // Stale-response guard — drop the payload if the user has since
      // typed more characters (a newer reqId is in flight).
      if (myReqId !== reqIdRef.current) return;
      // Feb 2026 — pin exact-id match to index 0 so Enter/Tab commits
      // it in one keystroke. Replaces the old auto-select useEffect
      // that stole typing mid-flight (defect D).
      const targetQ = q.trim().toLowerCase();
      const exactIdx = filtered.findIndex(
        (r) => String(r.item_id ?? '').trim().toLowerCase() === targetQ,
      );
      let ordered = filtered;
      if (exactIdx > 0) {
        ordered = filtered.slice();
        const [row] = ordered.splice(exactIdx, 1);
        ordered.unshift(row);
      }
      setResults(ordered);
      setQueryStatus(filtered.length === 0 ? status : (status === 'off_stock' ? status : ''));
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      console.error('Search failed:', err);
      setResults([]);
    }
    if (myReqId === reqIdRef.current) setLoading(false);
  }, [branch, type, searchEndpoint, excludeIds, minChars, includeFreight, resolveItem]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedLabel('');
    setOpen(true);

    if (value) {
      onSelect({ item_id: '', name: '', current_stock: 0 });
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleSelect = (item) => {
    setSelectedLabel(`${item.item_id} - ${item.name?.substring(0, 25)}`);
    setQuery('');
    setOpen(false);
    setResults([]);
    onSelect(item);
  };

  // Feb 2026 — Auto-select on exact-match has been REMOVED. It stole
  // typing mid-flight because short RM ids are prefixes of longer ones
  // (e.g. `INM_002` typing-path-prefix of `INM_0021x`). Explicit
  // selection is required — see `handleKeyDown` (Enter/Tab pick the
  // first result, which is the exact match if one exists because the
  // shared render pipeline pins exact matches to the top).

  // Enter/Tab → pick first result if the dropdown is showing matches.
  // Mirrors the "typed-but-not-picked" trap fix in Dispatch.js: instead of
  // expecting the user to mouse-click, give them a keyboard path that
  // commits the highlighted match.
  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && results.length > 0 && open) {
      e.preventDefault();
      handleSelect(results[0]);
    }
  };

  const displayValue = selectedLabel || query;
  const isDisabled = disabled || (!branch && !searchEndpoint);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Input
        data-testid={testId}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        disabled={isDisabled}
        placeholder={placeholder || (isDisabled ? "Select branch first" : `Type ${minChars}+ chars to search...`)}
        className="text-sm h-9"
      />
      
      {open
        && (results.length > 0 || loading || (query.length >= minChars && !loading))
        && dropdownRect
        && createPortal(
        <div
          ref={dropdownRef}
          className="z-50 bg-white border border-zinc-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          style={{
            position: 'fixed',
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
            // Radix Dialog sets `pointer-events: none` on body when
            // modal — must re-enable explicitly so clicks on this
            // portaled child don't silently fall through.
            pointerEvents: 'auto',
          }}
          // Stop the pointerdown from bubbling so Radix's
          // DismissableLayer (the dialog) doesn't see button clicks
          // here as "outside" interactions and race with handleSelect.
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="stock-search-dropdown"
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-zinc-400">Searching...</div>
          )}
          {!loading && results.length === 0 && query.length >= minChars && (
            <div
              className={`px-3 py-2 text-xs ${
                queryStatus === 'no_inventory' ? 'text-amber-700 font-medium' : 'text-zinc-500'
              }`}
              data-testid="stock-search-no-results"
              data-status={queryStatus || 'no_match'}
            >
              {queryStatus === 'no_inventory'
                ? 'No inventory'
                : searchEndpoint
                  ? 'Not found in master — check spelling'
                  : 'Not found in master — check spelling and branch'}
            </div>
          )}
          {results.map(item => (
            <button
              key={item.item_id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-100 flex justify-between items-center border-b border-zinc-50 last:border-0 ${item.is_freight ? "bg-amber-50/60" : item.off_stock ? "bg-zinc-50" : ""}`}
              onClick={() => handleSelect(item)}
              data-testid={`search-result-${item.item_id}`}
            >
              <span className="truncate flex-1">
                <span className="font-medium text-zinc-900">{item.item_id}</span>
                <span className="text-zinc-500 ml-1">- {item.name?.substring(0, 35)}</span>
              </span>
              {item.is_freight ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">
                  Service
                </span>
              ) : item.off_stock ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600 bg-zinc-200 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">
                  Off-stock
                </span>
              ) : (
                item.current_stock != null && (
                  <span className="text-xs font-mono text-emerald-600 ml-2 whitespace-nowrap">
                    {item.current_stock}
                  </span>
                )
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default StockItemSearch;
